import { wait, ErrorStringFromException, CancelableWait, isNullOrUndefined } from "../../utils/utils";
import { RETRY_SETTINGS, MAX_UNRESPONSIVENESS_WAIT_TIME_MILLISECONDS, RETRY_SETTINGS_REQUEST_TIMED_OUT } from "../../AppConstants";
import { ILogServiceEvent } from "../interfaces/ILogServiceEvent";
import { BatchTranscriptionClientErrorCode } from "@1js/al-voice-client";

// Converts from 100-nanoseconds to seconds.
export function secFrom100nsec(nsec100: number) {
    return 0.0000001 * nsec100;
}

// Converts from 100-nanoseconds to milliseconds.
export function msFrom100nsec(nsec100: number) {
    return 0.0001 * nsec100;
}

export function secFromMs(ms: number) {
    return ms/1000;
}

/** This enum is added to track the caller service which is getting timeout
  while using RetryServiceCallUntilSuccessOrTimeout utility.
 */
export enum RetryServiceCaller {
  InitalizeBatchTranscriptionClientWithSetAnnotation,
  InitalizeBatchTranscriptionClient,
  BatchTranscriptionRequestInOldFileUploadFlow,
  BatchTranscriptionRequestInNewFileUploadFlow,
  AnalyzeAudioFromDownloadUrl,
  AnalyzeUploadedAudio,
  MakeGraphGetRequest,
  ExceptionRetryHandlerExecute,
  OfficeFirstPartyAuthLoad,
  CallOFPAGetAccessToken,
  InitalizeCopilotLicensingClient
}

/** Note: caller param is added to track which throwing service call timeout.
This will help us investigations and drilldown issues faster.
*/
export function RetryServiceCallUntilSuccessOrTimeout<T>(
    logServiceEvent: ILogServiceEvent,
    throwingServiceCall: () => T,
    shouldRetryOnNull: boolean,
    caller: RetryServiceCaller,
    shouldRetryError?: (e: any) => boolean,
    shouldIgnoreTimeout?: () => boolean,
    timeoutMilliseconds: number = MAX_UNRESPONSIVENESS_WAIT_TIME_MILLISECONDS)
    : { promise: Promise<T>; continueToNextAttempt: () => void; cancel: () => void; } {

    let cancelled: boolean = false;
    let shouldContinueToNextAttempt = false;

    const promise =  new Promise<T>(async(resolve, reject) => {
      let retryCount: number = 0;
      let retryCountForRequestTimedoutError: number = 0;
      let isRequestTimedOutError: boolean = false;
      let lastError: any = "Failed even after " + RETRY_SETTINGS.maxRetries + " retry attempts. Giving up!";
      let timerId: any = null;
      let isTimedOut: boolean = false;
      let intervalForContinuationFlow: any = null;

      do {
          try {

            if (cancelled) {
                break;
            }

            if (isRequestTimedOutError) {
              isRequestTimedOutError = false;
              await wait(RETRY_SETTINGS_REQUEST_TIMED_OUT.retryWaitMilliseconds);
            }

            else if (retryCount > 0) {
                await wait(RETRY_SETTINGS.retryWaitMilliseconds);
            }

            const response: T = await new Promise<T>(async (resolve, reject) => {
            try {
                if (shouldIgnoreTimeout == null || !shouldIgnoreTimeout()) {

                  //clear any pending timer before starting new timer.
                  if (!isNullOrUndefined(timerId)) {
                    clearTimeout(timerId);
                    timerId = null;
                  }

                  isTimedOut = false;

                  timerId = setTimeout(function() {
                      isTimedOut = true;
                      timerId = null;
                      reject("Service call timed out after " + timeoutMilliseconds + " milliseconds" + ", entry point = " + caller);
                  }, timeoutMilliseconds);
                }

                if ((shouldIgnoreTimeout != null && shouldIgnoreTimeout()) || timeoutMilliseconds > MAX_UNRESPONSIVENESS_WAIT_TIME_MILLISECONDS) {

                  //clear any pending continuation interval before starting new interval.
                  if (!isNullOrUndefined(intervalForContinuationFlow)) {
                    clearInterval(intervalForContinuationFlow);
                    intervalForContinuationFlow = null;
                  }

                  shouldContinueToNextAttempt = false;

                  intervalForContinuationFlow = window.setInterval(() => {
                    if (shouldContinueToNextAttempt) {
                      reject("drop this request and continue to next request for entry point = " + caller);
                    } else if (cancelled) {
                      reject('request has been canceled.');
                    }
                  }, 10000);
                }

                resolve(await throwingServiceCall());
            } catch (error) {
                lastError = error;
                reject(error);
            }
            });

            if (response == null && shouldRetryOnNull) {
                logServiceEvent.AddResponse({ isSuccessful: false, message: "null", isFinalResponse: false });
                continue;
            }

              logServiceEvent.AddResponse({ isSuccessful: true, message: "Success", isFinalResponse: true });
              return resolve(response);
          } catch (e: any) {
              lastError = e;

              if (!isNullOrUndefined(e) && e.code == BatchTranscriptionClientErrorCode.REQUEST_TIMEOUT_ERROR) {
                retryCount--;
                isRequestTimedOutError = true;

                //fail REQUEST TIMEDOUT ERROR, after retry threshold is crossed.
                if (retryCountForRequestTimedoutError++ >= RETRY_SETTINGS_REQUEST_TIMED_OUT.maxRetries) {
                  cancelled = true; // setting cancelled to true, to make this error non retryable.
                }
              }

              else if (shouldContinueToNextAttempt) {
                retryCount--; // to make sure, this retry is not considered as it is dropped.
              }
              const isRetryable: boolean = !cancelled && (shouldContinueToNextAttempt || isTimedOut || (shouldRetryError == null) || shouldRetryError(e));
              logServiceEvent.AddResponse({ isSuccessful: false, message:  ErrorStringFromException(e), isFinalResponse: !isRetryable});
              isTimedOut = false;
              shouldContinueToNextAttempt = false;
              // If the caller doesn't want us to retry this error, throw the error back to the caller.
              if (!isRetryable) {
                  reject(e);
              }
          } finally {
            // clear running interval and timer.
            if (!isNullOrUndefined(intervalForContinuationFlow)) {
              clearInterval(intervalForContinuationFlow);
              intervalForContinuationFlow = null;
            }
            if (!isNullOrUndefined(timerId)) {
              clearTimeout(timerId);
              timerId = null;
            }

            //clean up retry count for Timed out Error in case of any other error, where retry is required.
            retryCountForRequestTimedoutError = 0;
          }

      } while(retryCount++ < RETRY_SETTINGS.maxRetries);

      logServiceEvent.AddResponse({isSuccessful: false, message: ErrorStringFromException(lastError), isFinalResponse: true});

      // clear interval and timer on giving up all attempts.
      if (!isNullOrUndefined(intervalForContinuationFlow)) {
        clearInterval(intervalForContinuationFlow);
        intervalForContinuationFlow = null;
      }

      if (!isNullOrUndefined(timerId)) {
        clearTimeout(timerId);
        timerId = null;
      }
      reject(lastError);
  })

  return ({
    promise: promise,
    continueToNextAttempt: () => {
      shouldContinueToNextAttempt = true;
    },
    cancel: () => {
      cancelled = true;
    }
  })
}

/**
 * Utility method to call method with multiple attempts to succeed and having a timeout
 * @param asyncMethodCall Method that should be attempted to call
 * @param attemptCount Number of attempt to be made
 * @param attemptWaitMilliSeconds Wait time between each attemp, first attemp will be executed without wait
 * @param timeoutForAllAttempts Total time before timeout to complete all attempts
 * @param preCallProcessing Caller processing from method call
 * @param postSuccessProcessing Caller processing after successful call
 * @param postFailureProcessing Caller processing after failed call
 * @returns Response resolved from asyncMethodCall
 */
export function AttemptsWithTimeoutCall<T>(
    asyncMethodCall: () => T,
    attemptCount: number = RETRY_SETTINGS.maxRetries,
    attemptWaitMilliSeconds: number = RETRY_SETTINGS.retryWaitMilliseconds,
    timeoutForAllAttempts: number = MAX_UNRESPONSIVENESS_WAIT_TIME_MILLISECONDS,
    preCallProcessing: (attempNumber: number, lastError: any | null) => void = () => { },
    postSuccessProcessing: (methodResponse: T) => void = () => { },
    postFailureProcessing: (methodError: any) => void = () => { }
): Promise<T> {
    return new Promise<T>(async (resolve, reject) => {
      let waitObjTimeoutForAllAttempts: { promise: Promise<void>, cancel: () => void} | null = null;

        try {
            let attemptNumber = 1;
            let lastError = null;

            // There can be a scenario where reject can be called post timeout,
            // for already resolved/rejected promise
            // Although, there won't be any impact on caller but post processing
            // method may called twice
            // Using these 2 variables to avoid it
            // IsCompleted will be true if request is already resolved or rejected
            // isTimedOut will be true if request rejected due to timeout.
            let isCompleted = false;
            let isTimedOut = false;
            waitObjTimeoutForAllAttempts = CancelableWait(timeoutForAllAttempts);
            waitObjTimeoutForAllAttempts.promise.then(() => {
                // Wait time is over, if call is not already completed
                // then it should be timed out,
                // If already resolved then do nothing
                if (!isCompleted) {
                    isTimedOut = true;
                    waitObjTimeoutForAllAttempts = null;
                    lastError = new Error("Method call timed out after " + timeoutForAllAttempts + " milliseconds");
                    postFailureProcessing(lastError);
                    reject(lastError);
                }
            })
                .catch(function (error) {
                    reject(error);
                });

            while (attemptNumber <= attemptCount && !isTimedOut) {
                preCallProcessing(attemptNumber, lastError);
                try {
                    const methodResponse: T = await asyncMethodCall();
                    // Resolve only if not already timed out
                    if (!isTimedOut) {
                        if (!isNullOrUndefined(waitObjTimeoutForAllAttempts)) {
                          waitObjTimeoutForAllAttempts!.cancel();
                          waitObjTimeoutForAllAttempts = null;
                        }
                        isCompleted = true;
                        postSuccessProcessing(methodResponse);
                        resolve(methodResponse);
                        return;
                    }
                } catch (error: any) {
                    lastError = error;
                }
                await wait(attemptWaitMilliSeconds);
                attemptNumber++;
            }

            // Reject only if not already timedout
            if (!isTimedOut) {
                if (!isNullOrUndefined(waitObjTimeoutForAllAttempts)) {
                  waitObjTimeoutForAllAttempts!.cancel();
                  waitObjTimeoutForAllAttempts = null;
                }
                isCompleted = true;
                postFailureProcessing(lastError);
                reject(lastError);
            }
        } catch (error: any) {
            // If any processing method throws error,
            // It should be handled here
            reject(error);
        } finally {
          if (!isNullOrUndefined(waitObjTimeoutForAllAttempts)) {
            waitObjTimeoutForAllAttempts!.cancel();
            waitObjTimeoutForAllAttempts = null;
          }
        }
    });
}

// Parse a number from the string representation of SpeakerId that the speech service gives us.
// e.g. "Guest_1" => 1
export function GetNumericSpeakerIdFromSpeakerId(speakerId?: string): number | undefined {
    if (speakerId == null)
      return undefined;

    // Get groups of digits from the input string.
    const matches = speakerId.match(/\d+/);

    if (matches == null || matches.length == 0)
      return undefined;

    // Return the first group of digits we find as number.
    return parseInt(matches[0]);
  }